import { depthInputValue, forceInputValue } from './units';

// Calibration settings as the backend stores them in a revision (load_test.py,
// _normalize_cal_settings), and the form the Calibration window edits them in.
//
// Stored shape, numbers or null throughout:
//   { loads: { design, test, max },                           kips
//     loadcell: { serial, factor },                           factor in lbs/digit
//     lvdts: [{ channel, serial, sensitivity, unit }] x 4,    unit mV/inch | mV/mm
//     strain_gauges: [{ channel, serial, factor, depth_m, excluded }] }
//
// The draft keeps what was typed as text, so a half-entered number or a cleared
// field isn't coerced while the user is still editing.

// The logger reports SG_digits(1)..(16) and four LVDTs.
export const MAX_STRAIN_GAUGES = 16;
export const PORTS = Array.from({ length: MAX_STRAIN_GAUGES }, (_, i) => i + 1);
export const LVDT_CHANNELS = [1, 2, 3, 4];
export const LVDT_UNITS = ['mV/inch', 'mV/mm'];
// The loads a test is run against, in the order they are shown. `color` (a
// theme palette key) and `dash` are how each one's line is drawn on the load
// graph; the Readings panel shows the same line next to the value. All three
// are dotted, so on the graph it is the name on each line, not only its colour,
// that tells them apart.
export const TEST_LOADS = [
  { key: 'design', label: 'Design load', short: 'Design', color: 'success', dash: '2 3' },
  { key: 'test', label: 'Test load', short: 'Test', color: 'warning', dash: '2 3' },
  { key: 'max', label: 'Max load', short: 'Max', color: 'error', dash: '2 3' },
];

// channel = the logger port (its SG_digits(n) column) the gauge is wired to,
// which is how the strain graph identifies it. depth is the text as typed in
// the current unit; depthM is the same value in metres, which is what is stored
// and what survives a unit flip.
export const blankGauge = () => ({ channel: null, serial: '', depth: '', depthM: null, factor: '', excluded: false });
export const isFilled = (gauge) => Boolean(gauge?.serial?.trim());

// The list always ends in exactly one empty row to type the next gauge into
// (unless all 16 ports are used), so clearing the last gauge doesn't stack up
// blank rows.
export function withNextSlot(rows) {
  const trimmed = [...rows];
  while (trimmed.length > 0 && !isFilled(trimmed[trimmed.length - 1])) trimmed.pop();
  if (trimmed.length < MAX_STRAIN_GAUGES) trimmed.push(blankGauge());
  return trimmed;
}

// Give each filled gauge without a port the lowest one not already taken, in
// row order, and clear the port off emptied rows so it frees up.
export function withPorts(rows) {
  const used = new Set(rows.filter(isFilled).map((g) => g.channel).filter(Boolean));
  let next = 1;
  return rows.map((g) => {
    if (!isFilled(g)) return g.channel == null ? g : { ...g, channel: null };
    if (g.channel) return g;
    while (used.has(next)) next += 1;
    used.add(next);
    return { ...g, channel: next };
  });
}

const asText = (value) => (value === null || value === undefined ? '' : String(value));

// Blank is "not set". Anything else goes up as a number; text that isn't one is
// sent as typed so the server's message can name the field.
const asNumber = (text) => {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : trimmed;
};

const asSerial = (text) => String(text ?? '').trim() || null;

export function draftFromSettings(settings, units) {
  const lvdts = new Map((settings?.lvdts || []).map((l) => [l.channel, l]));
  return {
    // Like a gauge's depth: `text` is what is typed in the current unit, `kips`
    // the same load in the stored unit.
    loads: Object.fromEntries(TEST_LOADS.map(({ key }) => {
      const kips = settings?.loads?.[key] ?? null;
      return [key, { text: forceInputValue(kips, units), kips }];
    })),
    loadcell: {
      serial: asText(settings?.loadcell?.serial),
      factor: asText(settings?.loadcell?.factor),
    },
    lvdts: LVDT_CHANNELS.map((channel) => {
      const lvdt = lvdts.get(channel);
      return {
        channel,
        serial: asText(lvdt?.serial),
        sensitivity: asText(lvdt?.sensitivity),
        unit: lvdt?.unit || LVDT_UNITS[0],
      };
    }),
    gauges: withNextSlot((settings?.strain_gauges || []).map((g) => ({
      channel: g.channel,
      serial: asText(g.serial),
      depth: depthInputValue(g.depth_m, units),
      depthM: g.depth_m ?? null,
      factor: asText(g.factor),
      excluded: Boolean(g.excluded),
    }))),
  };
}

export function settingsFromDraft(draft) {
  return {
    loads: Object.fromEntries(TEST_LOADS.map(({ key }) => [key, draft.loads[key].kips ?? null])),
    loadcell: {
      serial: asSerial(draft.loadcell.serial),
      factor: asNumber(draft.loadcell.factor),
    },
    lvdts: draft.lvdts.map((l) => ({
      channel: l.channel,
      serial: asSerial(l.serial),
      sensitivity: asNumber(l.sensitivity),
      unit: l.unit,
    })),
    strain_gauges: withPorts(draft.gauges.filter(isFilled))
      .map((g) => ({
        channel: g.channel,
        serial: asSerial(g.serial),
        factor: asNumber(g.factor),
        depth_m: g.depthM ?? null,
        excluded: Boolean(g.excluded),
      }))
      .sort((a, b) => a.channel - b.channel),
  };
}

// Whether a draft differs from the settings it was opened from. Both sides go
// through the same conversion, so a value that only changed its spelling on the
// way into the form (1.50 -> 1.5) doesn't count as an edit.
export function draftDiffers(draft, settings, units) {
  return JSON.stringify(settingsFromDraft(draft))
    !== JSON.stringify(settingsFromDraft(draftFromSettings(settings, units)));
}

const sameNumber = (text, number) => String(text).trim() !== '' && Number(text) === number;

/**
 * Bring a draft up to date with the sensors' own calibration records (the
 * settings the test's box would give today, from Equipment > Load Testing).
 *
 * Only what those records know is touched. For each sensor assigned to the box
 * the serial number is taken, and its factor when the record has one, so a
 * factor typed here for a sensor with no calibration on file is kept. Test
 * loads, gauge depths and exclude flags, and any sensor entered here that isn't
 * assigned to the box, stay as they are.
 *
 * Returns { draft, changed, assigned }: the updated draft, the names of the
 * sensors whose values changed, and whether the box has any sensors assigned.
 */
export function mergeSensorRecords(draft, records) {
  const changed = [];

  let loadcell = draft.loadcell;
  const recLoadcell = records?.loadcell;
  if (recLoadcell?.serial) {
    const next = {
      serial: recLoadcell.serial,
      factor: recLoadcell.factor != null && !sameNumber(loadcell.factor, recLoadcell.factor)
        ? asText(recLoadcell.factor) : loadcell.factor,
    };
    if (next.serial !== loadcell.serial.trim() || next.factor !== loadcell.factor) changed.push('load cell');
    loadcell = next;
  }

  const recLvdts = new Map((records?.lvdts || []).filter((l) => l.serial).map((l) => [l.channel, l]));
  const lvdts = draft.lvdts.map((lvdt) => {
    const rec = recLvdts.get(lvdt.channel);
    if (!rec) return lvdt;
    const takeFactor = rec.sensitivity != null
      && !(sameNumber(lvdt.sensitivity, rec.sensitivity) && lvdt.unit === rec.unit);
    const next = {
      ...lvdt,
      serial: rec.serial,
      sensitivity: takeFactor ? asText(rec.sensitivity) : lvdt.sensitivity,
      unit: takeFactor ? rec.unit || lvdt.unit : lvdt.unit,
    };
    if (next.serial !== lvdt.serial.trim() || takeFactor) changed.push(`LVDT ${lvdt.channel}`);
    return next;
  });

  // A gauge is matched by serial number first (it may have been moved to another
  // port here), then by port.
  const recGauges = (records?.strain_gauges || []).filter((g) => g.serial);
  const gauges = draft.gauges.filter(isFilled).map((g) => ({ ...g }));
  recGauges.forEach((rec) => {
    const bySerial = gauges.find((g) => g.serial.trim() === rec.serial);
    const target = bySerial || gauges.find((g) => g.channel === rec.channel);
    if (!target) {
      gauges.push({ ...blankGauge(), channel: rec.channel, serial: rec.serial, factor: asText(rec.factor) });
      changed.push(`SG${rec.channel}`);
      return;
    }
    const takeFactor = rec.factor != null && !sameNumber(target.factor, rec.factor);
    if (target.serial.trim() !== rec.serial || takeFactor) changed.push(`SG${target.channel}`);
    target.serial = rec.serial;
    if (takeFactor) target.factor = asText(rec.factor);
  });

  return {
    draft: { ...draft, loadcell, lvdts, gauges: withPorts(withNextSlot(gauges)) },
    changed,
    assigned: Boolean(recLoadcell?.serial) || recLvdts.size > 0 || recGauges.length > 0,
  };
}
